import {
  focusFirstError,
  issuesFromApiError,
  messageFromApiError,
  toFieldErrors,
  type ApiIssue,
  type FieldErrors,
} from '@caterinx/api-client';
import {
  CERTIFICATION_LABELS,
  CUISINE_LABELS,
  CuisineSchema,
  DIETARY_OPTION_LABELS,
  DietaryOptionSchema,
  LEAD_TIME_MAX_DAYS,
  MAX_SERVICE_AREAS,
  SERVICE_TYPE_LABELS,
  ServiceTypeSchema,
  SupplierCertificationSchema,
  SupplierProfileInputSchema,
  leadTimeDaysFromHours,
  leadTimeHoursFromDays,
  type ServiceArea,
} from '@caterinx/shared-types';
import { Alert, AlertDescription, AlertTitle } from '@caterinx/ui/components/ui/alert';
import { Button } from '@caterinx/ui/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@caterinx/ui/components/ui/card';
import { Checkbox } from '@caterinx/ui/components/ui/checkbox';
import { Input } from '@caterinx/ui/components/ui/input';
import { Label } from '@caterinx/ui/components/ui/label';
import { Separator } from '@caterinx/ui/components/ui/separator';
import { Skeleton } from '@caterinx/ui/components/ui/skeleton';
import { Textarea } from '@caterinx/ui/components/ui/textarea';
import { useToast } from '@caterinx/ui/hooks/use-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { AlertCircle, ArrowLeft, ArrowRight, Loader2, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ChipMultiSelect } from '../components/ChipMultiSelect.js';
import { FileField } from '../components/FileField.js';
import {
  fetchMyProfile,
  formatCents,
  parseEuroToCents,
  saveDocuments,
  saveProfile,
  saveServiceAreas,
  submitForReview,
  type SupplierProfileResponse,
} from '../lib/supplier.js';

/**
 * Der Onboarding-Wizard — und zugleich der Profil-Editor.
 *
 * **Fünf Schritte, und nach jedem wird gespeichert.** Das ist die wichtigste Entscheidung an
 * dieser Seite: Die meisten Gastronomen füllen sie am Handy aus, zwischen zwei Schichten. Ein
 * Formular, das erst am Ende speichert, ist beim ersten Anruf verloren — und was einmal
 * verloren ging, wird nicht noch einmal eingegeben.
 *
 * Es ist bewusst **kein** getrennter „Onboarding"- und „Bearbeiten"-Modus. Zwei Ansichten auf
 * dieselben Felder wären zwei Formulare, die auseinanderlaufen; hier ist der Wizard schlicht
 * die Art, wie dieses Profil bearbeitet wird — beim ersten Mal wie beim zehnten.
 */
const STEPS = [
  { key: 'betrieb', title: 'Ihr Betrieb', description: 'Firmenname, Ansprechpartner, Anschrift' },
  { key: 'leistungen', title: 'Leistungen', description: 'Was Sie anbieten und für wen' },
  { key: 'kapazitaet', title: 'Kapazität', description: 'Gästezahl, Vorlauf, Mindestbestellwert' },
  { key: 'gebiete', title: 'Liefergebiete', description: 'Wohin Sie liefern' },
  { key: 'nachweise', title: 'Nachweise', description: 'Steuerdaten und Unterlagen' },
] as const;

export function ProfileWizardPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [stepIndex, setStepIndex] = useState(0);

  const { data: profile, isPending, isError } = useQuery({ queryKey: ['supplier-profile'], queryFn: fetchMyProfile });

  if (isPending) {
    return (
      <main className="mx-auto max-w-2xl space-y-4 p-4 sm:p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </main>
    );
  }

  if (isError || !profile) {
    return (
      <main className="mx-auto max-w-2xl p-4 sm:p-6">
        <Alert variant="destructive">
          <AlertTitle>Profil konnte nicht geladen werden</AlertTitle>
          <AlertDescription>Bitte laden Sie die Seite neu.</AlertDescription>
        </Alert>
      </main>
    );
  }

  // eslint-disable-next-line security/detect-object-injection -- `stepIndex` ist ein interner Zähler, begrenzt durch STEPS.length
  const step = STEPS[stepIndex];
  const isLastStep = stepIndex === STEPS.length - 1;

  const afterSave = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['supplier-profile'] });
    void queryClient.invalidateQueries({ queryKey: ['me'] });
    if (!isLastStep) {
      setStepIndex((current) => current + 1);
      // Nach dem Schrittwechsel nach oben — auf dem Handy steht man sonst mitten im
      // nächsten Formular und sieht dessen Überschrift nie.
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <main className="mx-auto max-w-2xl space-y-6 p-4 pb-32 sm:p-6">
      <header className="space-y-2">
        <Link to="/" className="text-muted-foreground inline-flex items-center gap-1 text-sm hover:underline">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Zurück zur Übersicht
        </Link>
        <h1 className="text-2xl font-semibold">Ihr Betriebsprofil</h1>
        <p className="text-muted-foreground text-sm">
          Schritt {stepIndex + 1} von {STEPS.length} — {step.description}
        </p>

        {/* Fortschritt. Ein Balken statt einer Zahl, weil „noch zwei" motiviert und „Schritt 3" nicht. */}
        <div
          className="bg-muted h-1.5 w-full overflow-hidden rounded-full"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={STEPS.length}
          aria-valuenow={stepIndex + 1}
          aria-label="Fortschritt im Profil"
        >
          <div
            className="bg-primary h-full transition-all"
            style={{ width: `${((stepIndex + 1) / STEPS.length) * 100}%` }}
          />
        </div>
      </header>

      <nav className="flex flex-wrap gap-1.5" aria-label="Schritte">
        {STEPS.map((candidate, index) => (
          <button
            key={candidate.key}
            type="button"
            onClick={() => setStepIndex(index)}
            aria-current={index === stepIndex ? 'step' : undefined}
            className={`min-h-9 rounded-full px-3 py-1 text-xs transition-colors ${
              index === stepIndex ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-accent'
            }`}
          >
            {candidate.title}
          </button>
        ))}
      </nav>

      {step.key === 'betrieb' && <CompanyStep profile={profile} onSaved={afterSave} />}
      {step.key === 'leistungen' && <ServicesStep profile={profile} onSaved={afterSave} />}
      {step.key === 'kapazitaet' && <CapacityStep profile={profile} onSaved={afterSave} />}
      {step.key === 'gebiete' && <AreasStep profile={profile} onSaved={afterSave} />}
      {step.key === 'nachweise' && (
        <DocumentsStep
          profile={profile}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ['supplier-profile'] });
            toast({ title: 'Nachweise gespeichert' });
          }}
        />
      )}

      {isLastStep && (
        <SubmitSection
          profile={profile}
          onSubmitted={() => {
            void queryClient.invalidateQueries({ queryKey: ['supplier-profile'] });
            void queryClient.invalidateQueries({ queryKey: ['me'] });
            navigate('/');
          }}
        />
      )}

      {stepIndex > 0 && (
        <Button
          type="button"
          variant="ghost"
          className="min-h-11"
          onClick={() => {
            setStepIndex((current) => current - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" /> Zurück
        </Button>
      )}
    </main>
  );
}

/* --------------------------------------------------------------------------------------------
 * Gemeinsames
 * ----------------------------------------------------------------------------------------- */

interface StepProps {
  profile: SupplierProfileResponse;
  onSaved: () => void;
}

/**
 * Prüft eine Teilmenge des Profils **mit demselben Schema, das der Server benutzt**.
 *
 * Das ist der Kern der Fehlerbehandlung an dieser Seite: keine zweite, nachgebaute Prüfung im
 * Formular, sondern `SupplierProfileInputSchema` aus `shared-types`. Eine nachgebaute Prüfung
 * wäre beim ersten Schemawechsel milder oder strenger als der Server — und dann stünde der
 * Lieferant wieder vor „Ungültige Angaben" für ein Formular, das lokal fehlerfrei aussah.
 */
function checkProfileInput(payload: unknown, fieldByPath?: Record<string, string>): FieldErrors | null {
  const parsed = SupplierProfileInputSchema.safeParse(payload);
  if (parsed.success) return null;

  const issues: ApiIssue[] = parsed.error.issues.map((issue) => ({
    path: issue.path.map((segment) => (typeof segment === 'number' ? segment : String(segment))),
    message: issue.message,
  }));
  return toFieldErrors(issues, fieldByPath);
}

/**
 * Speichern samt Fehlerbehandlung — in einem Hook, weil jeder der fünf Schritte dasselbe tut
 * und fünf Kopien davon fünf Gelegenheiten wären, die Fehlermeldung zu vergessen.
 *
 * ### Warum die Fehler an den Feldern hängen und nicht im Einblender
 *
 * Die erste Fassung zeigte einen einzigen Hinweis: „Ungültige Angaben." Das ist die Meldung
 * des Servers, wörtlich weitergereicht — und im Formular ist sie wertlos. Wer die
 * Postleitzahl vierstellig eingetragen hat, sucht danach in acht Feldern und findet nichts.
 *
 * Jetzt läuft es zweistufig: **vor** dem Absenden dieselbe Zod-Prüfung wie im Backend, und
 * **nach** einer Ablehnung die `issues` aus der Antwort. Beide landen über `toFieldErrors` im
 * selben Zustand und markieren dasselbe Eingabefeld. Der Einblender bleibt für das, wofür er
 * taugt: „hat nicht geklappt", ohne Ortsangabe.
 */
function useStepSave({
  validate,
  save,
  onSaved,
  fieldByPath,
}: {
  /** Prüfung vor dem Absenden. Gibt Feldfehler zurück oder `null`, wenn alles passt. */
  validate?: () => FieldErrors | null;
  save: () => Promise<void>;
  onSaved: () => void;
  /** Abweichende Zuordnung Schemapfad → Eingabefeld, etwa `leadTimeHours` → `leadTimeDays`. */
  fieldByPath?: Record<string, string>;
}) {
  const { toast } = useToast();
  const [errors, setErrors] = useState<FieldErrors>({});

  const mutation = useMutation({
    mutationFn: save,
    onSuccess: () => {
      setErrors({});
      toast({ title: 'Gespeichert' });
      onSaved();
    },
    onError: (error) => {
      const fromServer = toFieldErrors(issuesFromApiError(error), fieldByPath);
      setErrors(fromServer);
      focusFirstError(fromServer);

      toast({
        variant: 'destructive',
        title: 'Speichern fehlgeschlagen',
        description:
          Object.keys(fromServer).length > 0
            ? 'Bitte prüfen Sie die rot markierten Felder.'
            : // Kein Formularfehler, sondern Netz, Anmeldung oder ein 500er — hier ist der
              // Text des Servers das Beste, was wir haben.
              (messageFromApiError(error) ?? 'Bitte versuchen Sie es erneut.'),
      });
    },
  });

  const submit = (): void => {
    const found = validate?.() ?? null;
    if (found !== null && Object.keys(found).length > 0) {
      setErrors(found);
      focusFirstError(found);
      // Bewusst ohne Einblender: Der Fehler steht am Feld und als Liste über dem Knopf. Ein
      // Einblender obendrauf verdeckt auf dem Handy genau das, was man lesen soll.
      return;
    }
    setErrors({});
    mutation.mutate();
  };

  return { errors, submit, isSaving: mutation.isPending };
}

/**
 * Die Attribute, die ein fehlerhaftes Eingabefeld braucht.
 *
 * `aria-invalid` ist zugleich der Aufhänger der roten Umrandung (siehe `packages/ui`) — was
 * der Screenreader als ungültig meldet, ist damit genau das, was auch rot ist.
 */
function errorProps(errors: FieldErrors, field: string): Record<string, unknown> {
  // eslint-disable-next-line security/detect-object-injection -- `field` ist ein Literal aus dieser Datei, kein Wert aus einer Antwort
  if (errors[field] === undefined) return {};
  return { 'aria-invalid': true, 'aria-describedby': `${field}-error` };
}

/** Der Fehlertext zu einem Feld, falls es einen gibt. */
function errorFor(errors: FieldErrors, field: string): string | undefined {
  // eslint-disable-next-line security/detect-object-injection -- siehe `errorProps`
  return errors[field];
}

function StepCard({
  title,
  description,
  children,
  onSave,
  isSaving,
  errors = {},
  saveLabel = 'Speichern und weiter',
}: {
  title: string;
  description: string;
  children: React.ReactNode;
  onSave: () => void;
  isSaving: boolean;
  errors?: FieldErrors;
  saveLabel?: string;
}) {
  const messages = Object.entries(errors);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {children}
        <Separator />

        {/*
         * Die Sammelmeldung steht direkt über dem Knopf, nicht oben in der Karte: Dort liegt
         * der Daumen, wenn der Klick nichts bewirkt hat. Ein Hinweis am Seitenanfang wäre auf
         * dem Handy außerhalb des Bildschirms — unsichtbar genau in dem Moment, in dem er
         * gebraucht wird.
         */}
        {messages.length > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <AlertTitle>Bitte prüfen Sie diese Angaben</AlertTitle>
            <AlertDescription>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                {messages.map(([field, message]) => (
                  <li key={field}>{message}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        <Button type="button" className="min-h-11 w-full" disabled={isSaving} onClick={onSave}>
          {isSaving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <ArrowRight className="mr-2 h-4 w-4" aria-hidden="true" />
          )}
          {saveLabel}
        </Button>
      </CardContent>
    </Card>
  );
}

/* --------------------------------------------------------------------------------------------
 * Schritt 1 — Betrieb
 * ----------------------------------------------------------------------------------------- */

function CompanyStep({ profile, onSaved }: StepProps) {
  const [companyName, setCompanyName] = useState(profile.companyName);
  const [contactName, setContactName] = useState(profile.contactName);
  const [contactPhone, setContactPhone] = useState(profile.contactPhone ?? '');
  const [street, setStreet] = useState(profile.address.street);
  const [postalCode, setPostalCode] = useState(profile.address.postalCode);
  const [city, setCity] = useState(profile.address.city);
  const [websiteUrl, setWebsiteUrl] = useState(profile.websiteUrl ?? '');
  const [description, setDescription] = useState(profile.description);
  const [logoS3Key, setLogoS3Key] = useState(profile.logoS3Key);

  const payload = (): Record<string, unknown> => ({
    companyName,
    contactName,
    contactPhone: contactPhone || null,
    address: { street, postalCode, city, country: 'DE' },
    websiteUrl: websiteUrl || null,
    description,
    logoS3Key,
  });

  const { errors, submit, isSaving } = useStepSave({
    validate: () => checkProfileInput(payload()),
    save: () => saveProfile(payload()),
    onSaved,
  });

  return (
    <StepCard
      title="Ihr Betrieb"
      description="Diese Angaben sehen Kunden im Angebotsvergleich — bis auf Anschrift und Telefonnummer."
      onSave={submit}
      isSaving={isSaving}
      errors={errors}
    >
      <Field label="Firmenname" htmlFor="companyName" error={errorFor(errors, 'companyName')}>
        <Input
          id="companyName"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          required
          {...errorProps(errors, 'companyName')}
        />
      </Field>

      <Field label="Ansprechpartner" htmlFor="contactName" error={errorFor(errors, 'contactName')}>
        <Input
          id="contactName"
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
          {...errorProps(errors, 'contactName')}
        />
      </Field>

      <Field
        label="Telefon"
        htmlFor="contactPhone"
        hint="Nur für uns und — nach einem Zuschlag — für den Kunden."
        error={errorFor(errors, 'contactPhone')}
      >
        <Input
          id="contactPhone"
          type="tel"
          inputMode="tel"
          value={contactPhone}
          onChange={(e) => setContactPhone(e.target.value)}
          {...errorProps(errors, 'contactPhone')}
        />
      </Field>

      <Field label="Straße und Hausnummer" htmlFor="street" error={errorFor(errors, 'street')}>
        <Input
          id="street"
          value={street}
          onChange={(e) => setStreet(e.target.value)}
          autoComplete="street-address"
          {...errorProps(errors, 'street')}
        />
      </Field>

      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-1">
          <Field label="PLZ" htmlFor="postalCode" error={errorFor(errors, 'postalCode')}>
            <Input
              id="postalCode"
              inputMode="numeric"
              maxLength={5}
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, ''))}
              autoComplete="postal-code"
              {...errorProps(errors, 'postalCode')}
            />
          </Field>
        </div>
        <div className="col-span-2">
          <Field label="Ort" htmlFor="city" error={errorFor(errors, 'city')}>
            <Input
              id="city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              autoComplete="address-level2"
              {...errorProps(errors, 'city')}
            />
          </Field>
        </div>
      </div>

      {/*
       * `type="text"` und **nicht** `type="url"`: Der Browser verlangt bei `url` ein Schema
       * und weist „catering.de" mit einer eigenen Blase ab, bevor unsere Prüfung überhaupt
       * greift. Genau diese Eingabe ist aber der Regelfall — das fehlende `https://` ergänzt
       * `normalizeWebsiteUrl` beim Speichern.
       */}
      <Field
        label="Website"
        htmlFor="websiteUrl"
        hint={'Optional. „catering.de" oder „www.catering.de" genügt — das https:// ergänzen wir.'}
        error={errorFor(errors, 'websiteUrl')}
      >
        <Input
          id="websiteUrl"
          type="text"
          inputMode="url"
          autoCapitalize="none"
          autoCorrect="off"
          placeholder="catering.de"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          {...errorProps(errors, 'websiteUrl')}
        />
      </Field>

      {/*
       * Freiwillig. Die Kurzbeschreibung war einmal Pflicht mit 40 Zeichen Mindestlänge und
       * damit die Stelle, an der ein Gastronom zwischen zwei Schichten abbrach — wer keinen
       * Werbetext parat hat, macht keinen, sondern hört auf. Der Hinweis wirbt weiter dafür.
       */}
      <Field
        label="Kurzbeschreibung (optional)"
        htmlFor="description"
        hint={
          description.trim().length === 0
            ? 'Was macht Ihren Betrieb aus? Kunden lesen das im Angebotsvergleich — ein paar Sätze helfen spürbar.'
            : `Kunden lesen das im Angebotsvergleich. (${description.trim().length} Zeichen)`
        }
        error={errorFor(errors, 'description')}
      >
        <Textarea
          id="description"
          rows={5}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Zum Beispiel: Seit 1998 in Düsseldorf. Regionale Küche, eigener Backbetrieb, Erfahrung mit Veranstaltungen bis 300 Gästen."
          {...errorProps(errors, 'description')}
        />
      </Field>

      <FileField
        kind="logo"
        label="Logo"
        hint="JPEG, PNG oder WebP. Erscheint im Angebotsvergleich neben Ihrem Angebot."
        accept="image/jpeg,image/png,image/webp"
        currentUrl={profile.logoUrl}
        onUploaded={setLogoS3Key}
      />
    </StepCard>
  );
}

/* --------------------------------------------------------------------------------------------
 * Schritt 2 — Leistungen
 * ----------------------------------------------------------------------------------------- */

function ServicesStep({ profile, onSaved }: StepProps) {
  const [serviceTypes, setServiceTypes] = useState(profile.serviceTypes);
  const [cuisines, setCuisines] = useState(profile.cuisines);
  const [dietaryOptions, setDietaryOptions] = useState(profile.dietaryOptions);
  const [certifications, setCertifications] = useState(profile.certifications);

  const { errors, submit, isSaving } = useStepSave({
    save: () => saveProfile({ serviceTypes, cuisines, dietaryOptions, certifications }),
    onSaved,
  });

  return (
    <StepCard
      title="Leistungen"
      description="Die Leistungsarten steuern, welche Anfragen Sie überhaupt erreichen."
      onSave={submit}
      isSaving={isSaving}
      errors={errors}
    >
      <Field
        label="Leistungsarten"
        hint="Mindestens eine. Ohne Auswahl erreicht Sie keine Ausschreibung."
        htmlFor={undefined}
      >
        <ChipMultiSelect
          options={ServiceTypeSchema.options}
          labels={SERVICE_TYPE_LABELS}
          value={serviceTypes}
          onChange={setServiceTypes}
        />
      </Field>

      <Field label="Küchenstil" htmlFor={undefined}>
        <ChipMultiSelect
          options={CuisineSchema.options}
          labels={CUISINE_LABELS}
          value={cuisines}
          onChange={setCuisines}
        />
      </Field>

      <Field
        label="Ernährungsformen"
        hint="Nur ankreuzen, was Sie zuverlässig bedienen können — daran hängen Zusagen an Gäste."
        htmlFor={undefined}
      >
        <ChipMultiSelect
          options={DietaryOptionSchema.options}
          labels={DIETARY_OPTION_LABELS}
          value={dietaryOptions}
          onChange={setDietaryOptions}
        />
      </Field>

      <Field
        label="Zertifikate"
        hint="Selbstauskunft für Ihr Profil. Die geprüften Nachweise kommen in Schritt 5."
        htmlFor={undefined}
      >
        <ChipMultiSelect
          options={SupplierCertificationSchema.options}
          labels={CERTIFICATION_LABELS}
          value={certifications}
          onChange={setCertifications}
        />
      </Field>
    </StepCard>
  );
}

/* --------------------------------------------------------------------------------------------
 * Schritt 3 — Kapazität
 * ----------------------------------------------------------------------------------------- */

function CapacityStep({ profile, onSaved }: StepProps) {
  const [minGuests, setMinGuests] = useState(String(profile.minGuests));
  const [maxGuests, setMaxGuests] = useState(profile.maxGuests === null ? '' : String(profile.maxGuests));
  /**
   * **Tage im Formular, Stunden im Datensatz.**
   *
   * Ein Gastronom denkt in Tagen — „drei Tage Vorlauf", nicht „72 Stunden". Gespeichert
   * bleibt die Stunde, weil das Matching gegen den echten Veranstaltungsbeginn rechnet und
   * „übermorgen 8 Uhr" etwas anderes ist als „übermorgen 20 Uhr". Die Umrechnung steht in
   * `shared-types`, damit Portal und Admin-Ansicht dieselbe Zahl zeigen.
   */
  const [leadTimeDays, setLeadTimeDays] = useState(String(leadTimeDaysFromHours(profile.leadTimeHours)));
  const [minOrder, setMinOrder] = useState(
    profile.minOrderNetCents === 0 ? '' : String(profile.minOrderNetCents / 100).replace('.', ',')
  );

  const payload = (): Record<string, unknown> => ({
    minGuests: Number(minGuests) || 0,
    maxGuests: maxGuests === '' ? null : Number(maxGuests),
    leadTimeHours: leadTimeHoursFromDays(Number(leadTimeDays) || 0),
    minOrderNetCents: minOrder === '' ? 0 : parseEuroToCents(minOrder),
  });

  const { errors, submit, isSaving } = useStepSave({
    validate: () => checkProfileInput(payload(), LEAD_TIME_FIELD),
    save: () => saveProfile(payload()),
    onSaved,
    fieldByPath: LEAD_TIME_FIELD,
  });

  return (
    <StepCard
      title="Kapazität"
      description="Diese Werte filtern Anfragen vor. Zu eng gesetzt heißt: weniger Ausschreibungen."
      onSave={submit}
      isSaving={isSaving}
      errors={errors}
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Ab Gästen" htmlFor="minGuests" error={errorFor(errors, 'minGuests')}>
          <Input
            id="minGuests"
            inputMode="numeric"
            value={minGuests}
            onChange={(e) => setMinGuests(e.target.value.replace(/\D/g, ''))}
            {...errorProps(errors, 'minGuests')}
          />
        </Field>
        <Field
          label="Bis Gäste"
          htmlFor="maxGuests"
          hint="Leer = keine Obergrenze."
          error={errorFor(errors, 'maxGuests')}
        >
          <Input
            id="maxGuests"
            inputMode="numeric"
            value={maxGuests}
            onChange={(e) => setMaxGuests(e.target.value.replace(/\D/g, ''))}
            {...errorProps(errors, 'maxGuests')}
          />
        </Field>
      </div>

      <Field
        label="Vorlaufzeit in Tagen"
        htmlFor="leadTimeDays"
        hint={`Wie viele Tage brauchen Sie zwischen Zuschlag und Veranstaltung? Anfragen mit kürzerem Vorlauf erreichen Sie nicht. 0 = auch kurzfristig, höchstens ${LEAD_TIME_MAX_DAYS} Tage.`}
        error={errorFor(errors, 'leadTimeDays')}
      >
        <Input
          id="leadTimeDays"
          inputMode="numeric"
          maxLength={2}
          placeholder="z. B. 3"
          value={leadTimeDays}
          onChange={(e) => setLeadTimeDays(e.target.value.replace(/\D/g, ''))}
          {...errorProps(errors, 'leadTimeDays')}
        />
      </Field>

      <Field
        label="Mindestbestellwert (netto, EUR)"
        htmlFor="minOrder"
        hint={
          minOrder === ''
            ? 'Leer = kein Mindestbestellwert.'
            : `Entspricht ${formatCents(parseEuroToCents(minOrder))} netto.`
        }
        error={errorFor(errors, 'minOrderNetCents')}
      >
        <Input
          id="minOrder"
          inputMode="decimal"
          placeholder="z. B. 350"
          value={minOrder}
          onChange={(e) => setMinOrder(e.target.value)}
          {...errorProps(errors, 'minOrderNetCents')}
        />
      </Field>
    </StepCard>
  );
}

/**
 * Die eine Stelle, an der Schemafeld und Eingabefeld verschiedene Namen tragen: Das Schema
 * kennt `leadTimeHours`, das Formular fragt nach Tagen. Ohne diese Zuordnung suchte
 * `focusFirstError` ein Eingabefeld `leadTimeHours`, das es nicht gibt — und der Fehler
 * bliebe unsichtbar.
 */
const LEAD_TIME_FIELD: Record<string, string> = { leadTimeHours: 'leadTimeDays' };

/* --------------------------------------------------------------------------------------------
 * Schritt 4 — Liefergebiete
 * ----------------------------------------------------------------------------------------- */

/**
 * Liefergebiete — deutschlandweit oder als zweistellige PLZ-Präfixe.
 *
 * **Deutschlandweit ist der Standard, und das ist eine Korrektur.** Die erste Fassung
 * verlangte mindestens ein PLZ-Gebiet und zeigte einem frisch registrierten Betrieb eine
 * leere Liste mit dem Hinweis, dass ihn ohne Gebiet keine Ausschreibung erreicht. Aus dem
 * Markt kam die Rückmeldung, dass die Mehrheit der Caterer ohnehin bundesweit arbeitet — die
 * Pflichteingabe war damit Arbeit für den Regelfall und eine Fehlerquelle obendrein (wer sie
 * überspringt, ist unsichtbar). Jetzt ist die Einschränkung die bewusste Entscheidung, nicht
 * die Voreinstellung.
 *
 * Zwei Ziffern statt fünf, weil dahinter ein DynamoDB-Item je Gebiet steht und die
 * Matching-Abfrage („wer liefert nach 40?") genau darauf läuft. Fünfstellig wären das
 * 8000 Items je Lieferant — und eine Auswahl, die niemand ausfüllt.
 */
function AreasStep({ profile, onSaved }: StepProps) {
  const [nationwide, setNationwide] = useState(profile.nationwide);
  const [areas, setAreas] = useState<ServiceArea[]>(profile.serviceAreas);
  const [newPlz2, setNewPlz2] = useState('');
  const { toast } = useToast();

  const { errors, submit, isSaving } = useStepSave({
    validate: () =>
      nationwide || areas.length > 0
        ? null
        : {
            newPlz2:
              'Bitte tragen Sie mindestens eine PLZ-Region ein — oder liefern Sie wieder deutschlandweit. Ohne Gebiet erreicht Sie keine Ausschreibung.',
          },
    save: () => saveServiceAreas(areas, nationwide),
    onSaved,
  });

  const addArea = (): void => {
    if (!/^\d{2}$/.test(newPlz2)) {
      toast({ variant: 'destructive', title: 'Zwei Ziffern', description: 'Zum Beispiel 40 für alle PLZ 40xxx.' });
      return;
    }
    if (areas.some((area) => area.plz2 === newPlz2)) {
      toast({ variant: 'destructive', title: 'Schon vorhanden', description: `Das Gebiet ${newPlz2} steht bereits.` });
      return;
    }
    if (areas.length >= MAX_SERVICE_AREAS) {
      toast({
        variant: 'destructive',
        title: 'Obergrenze erreicht',
        description: `Höchstens ${MAX_SERVICE_AREAS} Gebiete.`,
      });
      return;
    }
    setAreas([...areas, { plz2: newPlz2, travelSurchargeCents: 0 }].sort((a, b) => a.plz2.localeCompare(b.plz2)));
    setNewPlz2('');
  };

  return (
    <StepCard
      title="Liefergebiete"
      description="Standard ist deutschlandweit. Wer nur regional liefert, schränkt hier auf PLZ-Gebiete ein."
      onSave={submit}
      isSaving={isSaving}
      errors={errors}
    >
      {/*
       * Die Einschränkung ist der Sonderfall und steht deshalb als Häkchen, nicht als
       * Auswahl zwischen zwei gleichrangigen Möglichkeiten: Wer nichts anfasst, liefert
       * deutschlandweit — so, wie die Mehrheit der Betriebe tatsächlich arbeitet.
       */}
      <div className="border-input flex items-start gap-3 rounded-md border p-3">
        <Checkbox
          id="restrictAreas"
          className="mt-0.5"
          checked={!nationwide}
          onCheckedChange={(checked) => setNationwide(checked !== true)}
        />
        <div className="space-y-1">
          <Label htmlFor="restrictAreas" className="text-sm leading-snug font-medium">
            Nur bestimmte PLZ-Regionen beliefern
          </Label>
          <p className="text-muted-foreground text-xs">
            {nationwide
              ? 'Sie liefern deutschlandweit und bekommen Anfragen aus dem ganzen Bundesgebiet. Setzen Sie das Häkchen, um sich einzuschränken.'
              : 'Sie bekommen nur noch Anfragen aus den unten eingetragenen Gebieten.'}
          </p>
        </div>
      </div>

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Field
            label={nationwide ? 'Gebiet mit Anfahrtszuschlag hinzufügen' : 'Gebiet hinzufügen'}
            htmlFor="newPlz2"
            error={errorFor(errors, 'newPlz2')}
          >
            <Input
              id="newPlz2"
              inputMode="numeric"
              maxLength={2}
              placeholder="40"
              value={newPlz2}
              onChange={(e) => setNewPlz2(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addArea();
                }
              }}
              {...errorProps(errors, 'newPlz2')}
            />
          </Field>
        </div>
        <Button type="button" variant="outline" className="min-h-11" onClick={addArea}>
          <Plus className="mr-1 h-4 w-4" aria-hidden="true" /> Hinzufügen
        </Button>
      </div>

      {areas.length === 0 ? (
        nationwide ? (
          <Alert>
            <AlertTitle>Deutschlandweit</AlertTitle>
            <AlertDescription>
              Sie erhalten Anfragen aus dem ganzen Bundesgebiet. Einzelne Gebiete tragen Sie nur ein, wenn Sie dort
              einen Anfahrtszuschlag ansetzen wollen.
            </AlertDescription>
          </Alert>
        ) : (
          <Alert variant="destructive">
            <AlertTitle>Noch kein Liefergebiet</AlertTitle>
            <AlertDescription>
              Ohne Liefergebiet erreicht Sie keine Ausschreibung — das Gebiet ist der erste Filter im Matching.
            </AlertDescription>
          </Alert>
        )
      ) : (
        <ul className="divide-border divide-y rounded-md border">
          {areas.map((area) => (
            <li key={area.plz2} className="flex flex-wrap items-center gap-3 p-3">
              <span className="w-14 font-mono text-sm font-semibold">{area.plz2}xxx</span>
              <div className="flex flex-1 items-center gap-2">
                <Label htmlFor={`surcharge-${area.plz2}`} className="text-muted-foreground text-xs whitespace-nowrap">
                  Anfahrt (EUR)
                </Label>
                <Input
                  id={`surcharge-${area.plz2}`}
                  inputMode="decimal"
                  className="h-9 w-24"
                  value={
                    area.travelSurchargeCents === 0 ? '' : String(area.travelSurchargeCents / 100).replace('.', ',')
                  }
                  placeholder="0"
                  onChange={(e) =>
                    setAreas((current) =>
                      current.map((candidate) =>
                        candidate.plz2 === area.plz2
                          ? { ...candidate, travelSurchargeCents: parseEuroToCents(e.target.value) }
                          : candidate
                      )
                    )
                  }
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Gebiet ${area.plz2} entfernen`}
                onClick={() => setAreas((current) => current.filter((candidate) => candidate.plz2 !== area.plz2))}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <p className="text-muted-foreground text-xs">
        {nationwide ? 'Deutschlandweit' : `${areas.length} von ${MAX_SERVICE_AREAS} Gebieten`}. Der Anfahrtszuschlag ist
        ein Vorschlag für Ihre Kalkulation — verbindlich ist immer das Angebot, das Sie abgeben.
      </p>
    </StepCard>
  );
}

/* --------------------------------------------------------------------------------------------
 * Schritt 5 — Nachweise
 * ----------------------------------------------------------------------------------------- */

function DocumentsStep({ profile, onSaved }: StepProps) {
  const [vatId, setVatId] = useState(profile.compliance.vatId ?? '');
  const [taxNumber, setTaxNumber] = useState(profile.compliance.taxNumber ?? '');
  const [documents, setDocuments] = useState(profile.compliance.documents);

  /*
   * Der lokale Zustand wird bewusst **nicht** aus `profile` nachgezogen. Ein Upload schreibt
   * den neuen Schlüssel hierher, gespeichert wird er erst beim Klick auf „Nachweise
   * speichern" — ein zwischenzeitliches Neuladen des Profils (etwa beim Fokuswechsel) würde
   * ihn sonst wieder mit dem alten Wert überschreiben, und die gerade hochgeladene Datei
   * wäre still verloren.
   */

  const { errors, submit, isSaving } = useStepSave({
    save: () =>
      saveDocuments({
        vatId: vatId.trim() === '' ? null : vatId.trim().toUpperCase(),
        taxNumber: taxNumber.trim() === '' ? null : taxNumber.trim(),
        ...documents,
      }),
    onSaved,
  });

  return (
    <StepCard
      title="Nachweise"
      description="Diese Unterlagen prüfen wir vor der Freischaltung. Sie sind nur für caterinx sichtbar, nie für Kunden."
      onSave={submit}
      isSaving={isSaving}
      errors={errors}
      saveLabel="Nachweise speichern"
    >
      <Field
        label="USt-IdNr."
        htmlFor="vatId"
        hint="Mit Länderkürzel, z. B. DE123456789. Wir prüfen sie gegen VIES."
        error={errorFor(errors, 'vatId')}
      >
        <Input
          id="vatId"
          value={vatId}
          onChange={(e) => setVatId(e.target.value.toUpperCase())}
          placeholder="DE123456789"
          autoCapitalize="characters"
          {...errorProps(errors, 'vatId')}
        />
      </Field>

      <Field
        label="Steuernummer"
        htmlFor="taxNumber"
        hint="Falls Sie als Kleinunternehmer keine USt-IdNr. haben."
        error={errorFor(errors, 'taxNumber')}
      >
        <Input
          id="taxNumber"
          value={taxNumber}
          onChange={(e) => setTaxNumber(e.target.value)}
          {...errorProps(errors, 'taxNumber')}
        />
      </Field>

      <Separator />

      <FileField
        kind="trade_register"
        label="Handelsregisterauszug oder Gewerbeanmeldung"
        hint="PDF oder Foto."
        accept="application/pdf,image/jpeg,image/png,image/webp"
        currentUrl={profile.documentUrls.tradeRegisterUrl}
        onUploaded={(key) => setDocuments((current) => ({ ...current, tradeRegisterS3Key: key }))}
      />

      <FileField
        kind="food_hygiene"
        label="Nachweis Lebensmittelhygiene"
        hint="HACCP-Konzept oder Belehrung nach § 43 IfSG."
        accept="application/pdf,image/jpeg,image/png,image/webp"
        currentUrl={profile.documentUrls.foodHygieneUrl}
        onUploaded={(key) => setDocuments((current) => ({ ...current, foodHygieneS3Key: key }))}
      />

      <FileField
        kind="liability_insurance"
        label="Betriebshaftpflicht (optional)"
        hint="Kein Freischaltkriterium, im Schadensfall aber die erste Frage."
        accept="application/pdf,image/jpeg,image/png,image/webp"
        currentUrl={profile.documentUrls.liabilityInsuranceUrl}
        onUploaded={(key) => setDocuments((current) => ({ ...current, liabilityInsuranceS3Key: key }))}
      />
    </StepCard>
  );
}

/* --------------------------------------------------------------------------------------------
 * Abschluss
 * ----------------------------------------------------------------------------------------- */

function SubmitSection({ profile, onSubmitted }: { profile: SupplierProfileResponse; onSubmitted: () => void }) {
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: submitForReview,
    onSuccess: () => {
      toast({
        title: 'Zur Prüfung eingereicht',
        description: 'Wir melden uns, sobald Ihre Unterlagen geprüft sind.',
      });
      onSubmitted();
    },
    onError: (error) => {
      const data = isAxiosError(error) ? (error.response?.data as { message?: string }) : undefined;
      toast({ variant: 'destructive', title: 'Einreichen nicht möglich', description: data?.message });
    },
  });

  const isComplete = profile.missingFields.length === 0;
  const alreadySubmitted = profile.status === 'in_review' || profile.status === 'verified';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fertig?</CardTitle>
        <CardDescription>
          Wenn alles steht, geben Sie uns Bescheid. Wir prüfen Gewerbenachweis, USt-IdNr. und den Hygienenachweis und
          schalten Sie frei.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!isComplete && (
          <Alert>
            <AlertTitle>Es fehlt noch etwas</AlertTitle>
            <AlertDescription>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                {profile.missingFields.map((gap) => (
                  <li key={gap.field}>{gap.label}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {alreadySubmitted ? (
          <Alert>
            <AlertTitle>{profile.status === 'verified' ? 'Freigeschaltet' : 'Prüfung läuft'}</AlertTitle>
            <AlertDescription>
              {profile.status === 'verified'
                ? 'Sie erhalten passende Ausschreibungen per E-Mail. Änderungen am Profil sind jederzeit möglich.'
                : 'Ihre Unterlagen liegen uns vor. Wir melden uns.'}
            </AlertDescription>
          </Alert>
        ) : (
          <Button
            type="button"
            className="min-h-11 w-full"
            disabled={!isComplete || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            Zur Prüfung einreichen
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/* --------------------------------------------------------------------------------------------
 * Feldrahmen
 * ----------------------------------------------------------------------------------------- */

function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  /** Meldung zu diesem Feld. Ersetzt den Hinweis — sie ist die dringendere Information. */
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      {htmlFor ? (
        <Label htmlFor={htmlFor}>{label}</Label>
      ) : (
        // Ohne Eingabefeld mit `id` wäre ein `htmlFor` ein Verweis ins Leere — Screenreader
        // melden das als kaputtes Label. Chip-Gruppen bekommen deshalb eine reine Überschrift.
        <p className="text-sm leading-none font-medium">{label}</p>
      )}
      {children}
      {/*
       * Die `id` ist der Anker des `aria-describedby` am Eingabefeld, `role="alert"` sorgt
       * dafür, dass ein Screenreader die Meldung beim Erscheinen vorliest — sonst bliebe der
       * Fehler für genau die Nutzer stumm, die die rote Umrandung nicht sehen.
       */}
      {error !== undefined ? (
        <p id={htmlFor ? `${htmlFor}-error` : undefined} role="alert" className="text-destructive text-xs font-medium">
          {error}
        </p>
      ) : (
        hint && <p className="text-muted-foreground text-xs">{hint}</p>
      )}
    </div>
  );
}
